import { useState, useRef } from "react";
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { motion } from "framer-motion";
import { ArrowDown, Instagram } from "lucide-react";

export default function HeroSection() {
  const [status, setStatus] = useState(null); // 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const captchaRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);
    setErrorMessage("");

    const form = e.target;
    const formData = new FormData(form);
    const data = Object.fromEntries(formData);

    // Check if captcha is completed
    if (!captchaToken) {
      setStatus("error");
      setErrorMessage("Por favor verifica el captcha e intenta de nuevo.");
      setLoading(false);
      return;
    }

    // Web3Forms expects 'h-captcha-response'
    data['h-captcha-response'] = captchaToken;

    // Prevent Web3Forms from incorrectly identifying this as a Pro reCaptcha submission
    delete data['g-recaptcha-response'];

    const json = JSON.stringify(data);

    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: json
      });
      const result = await res.json();
      console.log("Web3Forms Submission Result:", result);

      if (result.success) {
        setStatus("success");
        form.reset();
        setCaptchaToken(""); // Reset local token
        if (captchaRef.current) captchaRef.current.resetCaptcha(); // Reset UI
      } else {
        setStatus("error");
        setErrorMessage(result.message || "Error desconocido del servidor.");
      }
    } catch (err) {
      console.error("Fetch Error:", err);
      setStatus("error");
      setErrorMessage("Error de conexión. Intenta más tarde.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1681310483042-64aa6776f112?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBhcm9tYXRpYyUyMGNhbmRsZSUyMGRhcmslMjBiYWNrZ3JvdW5kJTIwZ29sZCUyMGRldGFpbHxlbnwwfHx8fDE3NzIwNzk5OTV8MA&ixlib=rb-4.1.0&q=85"
          alt="Serena luxury candle"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-[#050505]" />
      </div>

      {/* Gold glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-[#D4AF37]/5 blur-[120px] animate-glow-pulse z-0" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center pt-24">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-[#D4AF37]/80 mb-6" data-testid="hero-tagline">
            Velas Premium &middot; Rituales Digitales &middot; Bienestar
          </p>
          <h1
            className="font-serif text-5xl md:text-7xl lg:text-8xl font-medium tracking-tight leading-[1.1] mb-6 text-white"
            data-testid="hero-heading"
          >
            Tu ritual,
            <br />
            <span className="italic text-[#D4AF37]">tu momento</span>
          </h1>
          <p
            className="text-base md:text-lg text-white/50 max-w-xl mx-auto leading-relaxed mb-10"
            data-testid="hero-description"
          >
            Cada vela Serena abre un portal de bienestar: escanea el QR y
            descubre afirmaciones, sonidos y rituales personalizados para ti.
          </p>
        </motion.div>

        {/* Waitlist form */}
        <motion.form
          id="waitlist"
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col items-center gap-4 max-w-md mx-auto"
          data-testid="waitlist-form"
        >
          <input type="hidden" name="access_key" value="ae7f687c-709c-49f9-9f94-5425e531d2fd" />
          <input type="hidden" name="subject" value="Nuevo Registro - Lista de Espera Serena" />
          <input type="hidden" name="from_name" value="Serena Landing Page" />

          <div className="flex flex-col sm:flex-row items-center gap-3 w-full">
            <input
              type="email"
              name="email"
              placeholder="tu@email.com"
              required
              className="w-full bg-white/5 border border-white/10 focus:border-[#D4AF37] px-5 py-3.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-1 focus:ring-[#D4AF37]/50 transition-all duration-300"
              data-testid="waitlist-email-input"
            />
            <button
              type="submit"
              disabled={loading}
              className="w-full sm:w-auto bg-[#D4AF37] text-black px-8 py-3.5 text-xs uppercase tracking-wider font-semibold hover:bg-[#C5A059] transition-all duration-300 disabled:opacity-50 whitespace-nowrap"
              data-testid="waitlist-submit-btn"
            >
              {loading ? "Enviando..." : "Unirme"}
            </button>
          </div>

          <HCaptcha
            sitekey="50b2fe65-b00b-4b9e-ad62-3ba471098be2"
            ref={captchaRef}
            theme="dark"
            onVerify={(token) => setCaptchaToken(token)}
            onExpire={() => setCaptchaToken("")}
          />
        </motion.form>

        {/* Status messages */}
        {status === "success" && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-[#D4AF37] text-sm mt-4"
            data-testid="waitlist-success-msg"
          >
            Bienvenida a Serena. Te avisaremos del lanzamiento.
          </motion.p>
        )}
        {status === "error" && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-red-400 text-sm mt-4 text-center"
            data-testid="waitlist-error-msg"
          >
            Algo salió mal:<br />
            <span className="font-mono text-xs opacity-80">{errorMessage}</span>
          </motion.p>
        )}

        {/* Store badges */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col items-center gap-3 mt-8"
        >
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/30">
            Descarga la app
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <a
              href="https://play.google.com/store/apps/details?id=mx.com.serenacandles.app&hl=es_MX"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="google-play-badge"
              className="flex items-center gap-2 bg-white/5 border border-white/10 hover:border-[#D4AF37]/50 px-5 py-2.5 transition-all duration-300 group"
            >
              <svg viewBox="0 0 24 24" className="w-5 h-5 text-[#D4AF37] group-hover:scale-110 transition-transform" fill="currentColor">
                <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 0 1 0 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.8 8.99l-2.3 2.3-8.636-8.632z"/>
              </svg>
              <div className="flex flex-col">
                <span className="text-[9px] uppercase tracking-wider text-white/40 leading-none">Disponible en</span>
                <span className="text-sm font-medium text-white leading-tight">Google Play</span>
              </div>
            </a>
            <span
              data-testid="app-store-badge"
              className="flex items-center gap-2 bg-white/5 border border-white/10 px-5 py-2.5 opacity-50 cursor-default"
            >
              <svg viewBox="0 0 24 24" className="w-5 h-5 text-white/40" fill="currentColor">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
              </svg>
              <div className="flex flex-col">
                <span className="text-[9px] uppercase tracking-wider text-white/40 leading-none">Próximamente</span>
                <span className="text-sm font-medium text-white/40 leading-tight">App Store</span>
              </div>
            </span>
          </div>
        </motion.div>

        {/* Instagram link */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mt-6 flex justify-center uppercase"
        >
          <a
            href="https://www.instagram.com/serena_candlesmx"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-white/40 hover:text-[#D4AF37] transition-colors duration-300 group"
          >
            <Instagram size={18} className="group-hover:scale-110 transition-transform" />
            <span className="text-[10px] tracking-widest font-medium group-hover:text-white/80 transition-colors">Síguenos en Instagram</span>
          </a>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="mt-16 md:mt-24 flex flex-col items-center gap-2"
        >
          <span className="text-[10px] uppercase tracking-[0.3em] text-white/30">
            Descubre
          </span>
          <ArrowDown size={16} className="text-white/30 animate-bounce" />
        </motion.div>
      </div>
    </section>
  );
}
